import { useMemo } from 'react';
import {
  CONTACT_DEFAULT_PRESET,
  CONTACT_LANDING_PRESET_ID,
  CONTACT_PLAY_CANDIDATES_PRESET_ID,
  CONTACT_PLAY_NOT_RESPONDED_PRESET_ID,
  CONTACT_PLAY_RESPONDED_PRESET_ID,
  CONTACT_PLAYS_PRESET_ID,
} from '@shared/flow/contact-view';
import { CONTACT_PLAY_SECTION_STATE_KEYS, type PlayPeopleSection } from '@shared/flow/play-people';
import type { FlowViewStateKey } from '../views/useViewState';

/**
 * The mount facts of ONE Contact component (plan
 * `plans/2026-09-10-001-plays-tab-filter-redesign-plan.md` D9).
 *
 * There is exactly one Contact table in Flow. What differs between where it
 * is mounted is not behaviour spread through the component as a dozen
 * `mountKind === …` ternaries (the shape this replaced — it had drifted into
 * three separate ideas of "which mount is this": `mountKind`,
 * `isGlobalMount`, and a bare `accountId` truthiness check) but a small set
 * of derived FACTS. Derive them once, here, and every call site in
 * `ContactFlowTable` reads a named field.
 *
 * Four INPUTS:
 *  - `accountId` — an account-scoped roster (drill-down / the main-Terra
 *    account page's Contacts tab);
 *  - `surface` — which product surface the table is under:
 *    - `'people'` (the default) — the People tab;
 *    - `'plays'` — the Plays tab's All People roster (no play selected);
 *    - the three "People in <Play>" SECTION surfaces (plan 2026-09-25-002
 *      U6 / D6), one per section of a selected play, each paired with a
 *      `playScope` prop: `'play-responded'` (Responded),
 *      `'play-not-responded'` (Not Responded) and `'play-candidates'` (Not
 *      in this play yet, plan 2026-09-24-005 U16). All three share every
 *      fact except their own working-state row, landing preset and
 *      namespaced `?view_<stateKey>=` URL param (D3) — three of them mount
 *      on one page, so none may read the page-global `?view=`;
 *  - `viewParam` — overrides the derived `?view=` URL key;
 *  - `consumeContactParam` — `false` for a roster nested inside another
 *    table's expanded band (neither reads nor writes `?contact=`).
 *
 * They are orthogonal on purpose: "the Plays tab" is a SURFACE, not a third
 * mount kind. The old `plays: { playId, bucket }` prop conflated the two and
 * made "the whole book under Plays" a play mount with a null play — the
 * scope now arrives as an ordinary, visible filter chip from the Plays list
 * (D8), so the surface is all the component needs to know.
 */
/** The product surface a Contact table is mounted under (see `ContactMount.surface`). */
export type ContactSurface = 'people' | 'plays' | 'play-candidates' | 'play-responded' | 'play-not-responded';

/** Which contact surface serves each "People in <Play>" section (D6). Narrower than `ContactSurface` (never `'people'`) so it type-checks directly against `ContactFlowTableProps.surface` (`Exclude<ContactSurface, 'people'>`). */
export const PLAY_SECTION_SURFACE: Record<PlayPeopleSection, Exclude<ContactSurface, 'people'>> = {
  responded: 'play-responded',
  not_responded: 'play-not-responded',
  not_in_play: 'play-candidates',
};

const SECTION_OF_SURFACE: Partial<Record<ContactSurface, PlayPeopleSection>> = {
  'play-responded': 'responded',
  'play-not-responded': 'not_responded',
  'play-candidates': 'not_in_play',
};

/** One of the three selected-play section surfaces (`PLAY_SECTION_SURFACE`'s values). */
export function isPlaySectionSurface(surface: ContactSurface): boolean {
  return SECTION_OF_SURFACE[surface] !== undefined;
}

export interface ContactMount {
  /** Account-scoped or not. The plays surface is never account-scoped (see `deriveContactMount`). */
  kind: 'global' | 'account';
  /** The account whose roster this is — `undefined` on a global mount; passed straight to `useFlowContacts`. */
  accountId?: string;
  /** `kind === 'global'`, named for the read sites that ask the question that way. */
  isGlobal: boolean;
  /** Which product surface the table is mounted under. */
  surface: ContactSurface;
  /** Which server-side working-state ROW this mount owns (one row per mount, never per account/play). */
  stateKey: FlowViewStateKey;
  /** The preset a rep lands on with no stored active/default view. */
  landingPresetId: string;
  /**
   * Restore the stored working half on first resolution.
   *
   * OFF for the account mount (a shared `contact_account` row must not replay
   * account A's filters onto account B — Annie Hale, 2026-08-27) and, since
   * plan 2026-10-06 item 1, OFF for the three play-SECTION mounts for the
   * same reason one dimension over: each section's row is shared across every
   * play AND every session, so a divergence a rep introduced once (a sort
   * click, the seeded "(Mine)" chip removed to look at everyone) came back as
   * a permanent orange "Modified" cluster in every play they opened
   * afterwards — `baseline` is recomputed clean on every mount while
   * `working` was restored verbatim, and "Modified" is the live diff of the
   * two.
   *
   * Both rows are still WRITTEN and both `defaultViewId` pointers are still
   * read (rung 3). What the sections get back in exchange, which the account
   * mount does not have, is `playSectionSessionViews.ts`: an in-session,
   * client-only cache that restores a section's state across a
   * collapse/reopen and a play switch, so plan 2026-09-25-003 U1's behaviour
   * survives without the cross-session lie.
   */
  restoreWorkingState: boolean;
  /**
   * Carry ad-hoc filters across a view switch.
   *
   * Tracks `restoreWorkingState` for the account mount but NOT for the
   * sections: a carry happens inside ONE mounted table, which is the same
   * session by construction, so it can never replay stale cross-session
   * state. The account mount's staleness was cross-ACCOUNT — same page, same
   * session — which is exactly what a carry does reach.
   */
  carryFilters: boolean;
  /** Drop `account_name` + the cross-join account columns: in a drill-down every row repeats the same value. */
  hideAccountColumns: boolean;
  /** Render the table's own search box (the global People mount defers to the omnibox). */
  showSearch: boolean;
  /** Owns the global scroll+flash jump to the `?contact=` row — exactly one mount may, or two behaviours double-handle one param. */
  ownsContactParam: boolean;
  /**
   * Reads AND writes `?contact=` (deep-link expansion, "Filters paused..."
   * banner, row-click write). True for every host contact table, including the
   * account-lens roster; false only for a roster nested in another table's
   * expanded band, which keeps its expansion local.
   */
  consumesContactParam: boolean;
  /**
   * Drop every Signal column (`CONTACT_SIGNAL_FIELD_KEYS`) from the rendered
   * table AND the column picker — a play-section mount is scoped by its own
   * section universe, so a Signal column would describe some OTHER play's
   * row (U16 / D4, D6). Render-only, like `hideAccountColumns`.
   */
  hideSignalColumns: boolean;
  /**
   * Render/offer the `play_*` columns (`CONTACT_PLAY_FIELD_KEYS`). Only the
   * play-section mounts send `play_id`, and the route 400s any `play_*`
   * sort/filter/group without it — so every other mount neither renders nor
   * offers them.
   */
  showPlayColumns: boolean;
  /**
   * The nuqs URL key this mount's view machine reads/writes (D3):
   * `'view'` everywhere except the play-section mounts, which get
   * `view_<stateKey>` so the three sections on one page never observe each
   * other's view (URL key and working-state row pinned together).
   */
  viewParam: string;
  /** Forwarded verbatim to `withRoleDefaults`/`buildRoleDefaultFilter`. */
  roleDefaultOpts: { mount: 'global' | 'account'; surface: ContactSurface };
}

export interface ContactMountInput {
  accountId?: string;
  surface?: ContactSurface;
  /** Overrides the derived `?view=` URL key (a roster mounted inside the Signals expanded row uses `view_contact_account`). */
  viewParam?: string;
  /** `false` for a roster nested in another table's expanded band: it neither reads nor writes `?contact=`. Default true. */
  consumeContactParam?: boolean;
}

/**
 * Any Plays-tab surface — the roster (`'plays'`) or one of the three
 * selected-play sections. The single check for the behaviour they agree on
 * (Custom Invite button, the Signal Subtype live option list); they differ
 * only in the derived facts (`stateKey`, landing preset, column gates).
 */
export function isPlaySurface(mount: Pick<ContactMount, 'surface'>): boolean {
  return mount.surface === 'plays' || isPlaySectionSurface(mount.surface);
}

const SECTION_LANDING_PRESET_ID: Record<PlayPeopleSection, string> = {
  responded: CONTACT_PLAY_RESPONDED_PRESET_ID,
  not_responded: CONTACT_PLAY_NOT_RESPONDED_PRESET_ID,
  not_in_play: CONTACT_PLAY_CANDIDATES_PRESET_ID,
};

/**
 * Pure derivation — the whole D9 table in one place. Unit-tested directly
 * (`__tests__/useContactMount.test.ts`); `useContactMount` below is only the
 * memo wrapper.
 *
 * `accountId` wins over `surface` for `kind`/`stateKey`/`landingPresetId`.
 * That combination is unreachable in the app (the Plays tab has no account
 * context), and account-first is the safe resolution: an account-scoped
 * table must never be handed the global mount's shared `contact_play` row or
 * its restore/carry behaviour. `surface` is still reported as asked, so the
 * role defaults stay consistent with the caller's intent.
 */
export function deriveContactMount({ accountId, surface = 'people', viewParam, consumeContactParam = true }: ContactMountInput): ContactMount {
  const kind: 'global' | 'account' = accountId ? 'account' : 'global';
  const isGlobal = kind === 'global';
  const onPlaySurface = isPlaySurface({ surface });
  // The section facts are account-first too: an (unreachable) account +
  // play-section combination is an ordinary account roster.
  const section = isGlobal ? SECTION_OF_SURFACE[surface] : undefined;
  const isSection = section !== undefined;

  const stateKey: FlowViewStateKey = kind === 'account'
    ? 'contact_account'
    : surface === 'plays' ? 'contact_play'
      // The shared record is `Record<…, string>` (the shared module must not
      // import the web-side `FlowViewStateKey` union); its three literals are
      // members of that union, pinned by useContactMount.test.ts.
      : section ? CONTACT_PLAY_SECTION_STATE_KEYS[section] as FlowViewStateKey
        : 'contact';

  const landingPresetId = kind === 'account'
    ? CONTACT_DEFAULT_PRESET.id
    : surface === 'plays' ? CONTACT_PLAYS_PRESET_ID
      : section ? SECTION_LANDING_PRESET_ID[section]
        : CONTACT_LANDING_PRESET_ID;

  return {
    kind,
    accountId,
    isGlobal,
    surface,
    stateKey,
    landingPresetId,
    restoreWorkingState: isGlobal && !isSection,
    carryFilters: isGlobal,
    hideAccountColumns: kind === 'account',
    // The global People mount is the only one whose search lives elsewhere
    // (Command Center's omnibox); a scoped roster and the plays surface both
    // keep the box (both Plays-tab surfaces).
    showSearch: kind === 'account' || onPlaySurface,
    ownsContactParam: consumeContactParam && isGlobal && surface === 'people',
    consumesContactParam: consumeContactParam,
    hideSignalColumns: isSection,
    showPlayColumns: isSection,
    viewParam: viewParam ?? (isSection ? `view_${stateKey}` : 'view'),
    roleDefaultOpts: { mount: kind, surface },
  };
}

/** Memoized `deriveContactMount` — a stable object identity so it can sit in a `useMemo`/`useCallback` dep list. */
export function useContactMount(input: ContactMountInput): ContactMount {
  const { accountId, surface, viewParam, consumeContactParam } = input;
  return useMemo(
    () => deriveContactMount({ accountId, surface, viewParam, consumeContactParam }),
    [accountId, surface, viewParam, consumeContactParam],
  );
}
